import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { switchMap, catchError, filter, take, finalize } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { Router } from "@angular/router";
import { ROUTES } from "src/app/utils/routes";
import { LoadingService } from "src/app/services/loading.service";
import { AUTH_ENDPOINTS, PIN } from "src/environments/api.constants";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private readonly refreshFailedSignal = '__REFRESH_FAILED__';
  private readonly skipGlobalLoaderHeader = 'X-Skip-Global-Loader';

  constructor(
    private authService: AuthService,
    private router: Router,
    private loadingService: LoadingService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    const skipGlobalLoader = req.headers.get(this.skipGlobalLoaderHeader) === 'true';
    let authReq = req;

    if (skipGlobalLoader) {
      authReq = req.clone({
        headers: req.headers.delete(this.skipGlobalLoaderHeader)
      });
    }

    if (token) {
      authReq = authReq.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
      });
    }

    if (!skipGlobalLoader) {
      this.loadingService.show();
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Si falla el refresh en sí, evitamos reintentos encadenados que pueden dejar la UI bloqueada.
          if (authReq.url.includes(AUTH_ENDPOINTS.refresh)) {
            this.authService.removeToken();
            this.authService.removeRefreshToken();
            this.authService.removeUserData();
            this.router.navigateByUrl(ROUTES.LOGIN);
            return throwError(() => error);
          }

          // Recover PIN puede devolver 401 por credenciales/PIN inválidos.
          // En ese caso se debe mostrar el mensaje en pantalla y no cerrar sesión.
          // Login también devuelve 401 por credenciales inválidas: no intentar refresh.
          if (authReq.url.includes(PIN.postRecoverPin) || authReq.url.includes(AUTH_ENDPOINTS.login)) {
            return throwError(() => error);
          }
          return this.handle401Error(authReq, next, skipGlobalLoader);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (!skipGlobalLoader) {
          this.loadingService.hide();
        }
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler, skipGlobalLoader: boolean): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((newTokens) => {
          this.isRefreshing = false;

          // guardo tokens en el servicio
          this.authService.saveTokens(newTokens);

          // notifico a los que estaban esperando
          this.refreshTokenSubject.next(newTokens.token);

          // reintento el request original
          const cloned = req.clone({
            setHeaders: { Authorization: `Bearer ${newTokens.token}` }
          });
          return next.handle(cloned);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(this.refreshFailedSignal);
          this.authService.removeToken();
          this.authService.removeRefreshToken();
          this.authService.removeUserData();
          this.router.navigateByUrl(ROUTES.LOGIN);
          return throwError(() => err);
        }),
        finalize(() => {
          if (!skipGlobalLoader) {
            this.loadingService.hide();
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          if (token === this.refreshFailedSignal) {
            return throwError(() => new Error('Refresh token failed'));
          }
          const cloned = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          });
          return next.handle(cloned);
        }),
        finalize(() => {
          if (!skipGlobalLoader) {
            this.loadingService.hide();
          }
        })
      );
    }
  }
}
